import api, {
  makePublicRequest,
  makeAuthenticatedRequest,
} from "../../../app/axios/apiClient";
import { Region, Country, State, City } from "../AddMspForm/types";
import { ContactDTO } from "../../common/types";

export type MspRecord = {
  id: number;
  msp_name: string;
  corporate_email: string;
  mobile_phone: string;
  status: number;
  created_at: string;
  updated_at: string;
  link: string;
};

export type FetchMspsParams = {
  page?: number;
  limit?: number;
  search?: string;
};

export type MspApiResponse = {
  status: string;
  message: string;
  data: MspRecord[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRows: number;
  };
};

export type AddLicensePayload = {
  licenses: number;
  transactionDetails?: {
    refId?: string;
    paymentMode?: string;
    paymentDate?: string;
    comments?: string;
  };
};

export type ReduceLicensePayload = {
  licenses: number;
};

export const fetchMsps = async (
  params: FetchMspsParams
): Promise<MspApiResponse> => {
  const { page = 1, limit = 10, search = "" } = params;
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/msps`,
      params: { page, limit, search },
      skipLoadingOverlay: true,
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch MSPs:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch MSPs");
  }
};

export const deleteMsp = async (id: number): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "DELETE",
      url: `/msps`,
      data: { id },
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to delete MSP:", error);
    throw new Error(error.response?.data?.message || "Failed to delete MSP");
  }
};

export const fetchCountries = async (): Promise<Country[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/countries`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load countries:", error);
    throw new Error(
      error.response?.data?.message || "Failed to load countries"
    );
  }
};

export const fetchStates = async (countryId: number): Promise<State[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/states/${countryId}`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load states:", error);
    throw new Error(error.response?.data?.message || "Failed to load states");
  }
};

export const fetchCities = async (stateId: number): Promise<City[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/cities/${stateId}`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load cities:", error);
    throw new Error(error.response?.data?.message || "Failed to load cities");
  }
};

export const fetchProducts = async (): Promise<[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/products`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load products:", error);
    throw new Error(error.response?.data?.message || "Failed to load products");
  }
};

export const fetchMSPPlans = async (productId: number): Promise<[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/plans/${productId}`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load plans:", error);
    throw new Error(error.response?.data?.message || "Failed to load plans");
  }
};

export const fetchRegionsByCountry = async (
  countryId: number
): Promise<Region[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/countries/regions/${countryId}`,
      headers: { accept: "application/json" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load regions by country:", error);
    throw new Error(
      error.response?.data?.message || "Failed to load regions by country"
    );
  }
};

export const createMsp = async (data: any): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "POST",
      url: `/msps`,
      data,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to create MSP:", error);
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create MSP. Please try again."
    );
  }
};

export const fetchMspInformation = async (id: number): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/msps/${id}`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to get MSP info:", error);
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to get MSP info. Please try again."
    );
  }
};

export const getMSPContact = async (
  mspId: number,
  mspContactId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/msps/${mspId}/contacts/${mspContactId}`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to get MSP contact info:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to get MSP contact info. Please try again."
    );
  }
};

export const updateMSPContact = async (
  mspId: number,
  mspContactId: number,
  apiPayload: ContactDTO
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/msps/${mspId}/contacts/${mspContactId}`,
      data: apiPayload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to update MSP contact info:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update MSP contact info. Please try again."
    );
  }
};

export const addMSPContact = async (
  mspId: number,
  apiPayload: ContactDTO
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "POST",
      url: `/msps/${mspId}/contacts`,
      data: apiPayload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add MSP contact:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add MSP contact. Please try again."
    );
  }
};

export const updateMsp = async (id: number, data: any) => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/msps/${id}`,
      data,
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to update MSP:", error);
    throw error;
  }
};

export const addMspProduct = async (data: any): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "POST",
      url: `/msps/add-products`,
      data,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add MSP product:", error);
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add MSP product. Please try again."
    );
  }
};

export const addLicensesToMSPSubscription = async (
  mspId: number,
  subscriptionId: number,
  payload: AddLicensePayload
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/msps/${mspId}/subscription/${subscriptionId}/add-license`,
      data: payload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add licenses to subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add licenses. Please try again."
    );
  }
};

export const reduceLicensesFromMSPSubscription = async (
  mspId: number,
  subscriptionId: number,
  payload: ReduceLicensePayload
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/msps/${mspId}/subscription/${subscriptionId}/reduce-license`,
      data: payload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to reduce licenses from subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to reduce licenses. Please try again."
    );
  }
};

export const cancelMspSubscription = async (
  mspId: number,
  subscriptionId: number
): Promise<any> => {
  try {
    const url = `/msps/${mspId}/subscription/${subscriptionId}/cancel`;

    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to cancel MSP subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to cancel subscription. Please try again."
    );
  }
};

export const activateMspSubscription = async (
  subscriptionId: number,
  mspId?: number
): Promise<any> => {
  try {
    const url = `/msps/subscription/${subscriptionId}/activate`;

    const response = await makeAuthenticatedRequest({
      method: "PATCH",
      url,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to activate subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to activate subscription. Please try again."
    );
  }
};

/**
 * Call server API to calculate cost for adding licenses.
 * Endpoint: GET /msps/{mspId}/subscription/{subscriptionId}/add-license/calculate-cost?licenses=...
 */
export const calculateAddLicenseCost = async (
  mspId: number,
  subscriptionId: number,
  licenses: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/msps/${mspId}/subscription/${subscriptionId}/add-license/calculate-cost`,
      params: { licenses },
      headers: { accept: "application/json" },
      skipLoadingOverlay: true,
    });
    // return full response data so caller can pick fields (amount, breakdown, taxes etc)
    return response.data;
  } catch (error: any) {
    console.error("Failed to calculate add-license cost:", error);
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to calculate cost. Please try again."
    );
  }
};

/**
 * Soft delete a specific MSP contact.
 * Endpoint: DELETE /msps/{mspId}/contacts/{contactId}
 */
export const deleteMSPContact = async (
  mspId: number,
  contactId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "DELETE",
      url: `/msps/${mspId}/contacts/${contactId}`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to delete MSP contact:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete MSP contact. Please try again."
    );
  }
};

export default api;
